import { useState } from 'react';
import {
  X,
  Tv,
  MonitorSmartphone,
  CalendarDays,
  Globe,
  Building2,
  Car,
  LayoutGrid,
  Hash,
  Users,
} from 'lucide-react';
import { CustomSelect } from '@/components/CustomSelect';
import { FlagIcon } from '@/components/FlagIcon';
import { PlatformIcon } from '@/components/PlatformIcon';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { CONTENT_ALIGN_PX } from '@/components/layout/layoutConstants';
import {
  TeamName,
  VALID_TEAM_NAMES,
  loadSelectedTeam,
  applyTeamDefaults,
  saveSelectedTeam,
} from '@/utils/columnConfig';

interface FilterDrawerProps {
  isOpen: boolean;
  filters: {
    channel: string;
    platform: string;
    quarter: string;
    market: string;
    business_unit: string;
    nameplate: string;
    format: string;
    plan_id: string;
  };
  onFilterChange: (filters: FilterDrawerProps['filters']) => void;
  availableOptions: Record<string, string[]>;
  activeFilterCount: number;
  onClearFilters: () => void;
  onTeamChange: () => void;
}

export function FilterDrawer({
  isOpen,
  filters,
  onFilterChange,
  availableOptions,
  activeFilterCount,
  onClearFilters,
  onTeamChange,
}: FilterDrawerProps) {
  const [selectedTeam, setSelectedTeam] = useState<TeamName | null>(loadSelectedTeam);

  const handleTeamChange = (v: string) => {
    if (v) {
      applyTeamDefaults(v as TeamName);
      setSelectedTeam(v as TeamName);
    } else {
      saveSelectedTeam(null);
      setSelectedTeam(null);
    }
    onTeamChange();
  };

  const totalActiveFilters = activeFilterCount + (selectedTeam ? 1 : 0);

  const handleClearAll = () => {
    onClearFilters();
    handleTeamChange('');
  };

  return (
    <div
      className={`w-full grid transition-[grid-template-rows] duration-300 ease-in-out ${
        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      }`}
    >
      {/* overflow-visible when open so dropdown menus aren't clipped; hidden when closed for animation */}
      <div className={`min-h-0 min-w-0 ${isOpen ? 'overflow-visible' : 'overflow-hidden'}`}>
        <div
          className={`transition-[opacity,transform] duration-300 ease-in-out ${
            isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
          }`}
        >
          {/* Filter row — left edge on the shared content line */}
          <div
            className="w-full pt-3 pb-3 pr-4 flex items-center gap-2"
            style={{ paddingLeft: `${CONTENT_ALIGN_PX}px` }}
          >
            {[
              {
                label: 'Channel',
                tooltip: 'Channel — Paid Social, Paid Search, etc.',
                value: filters.channel,
                options: availableOptions.channel ?? [],
                onChange: (v: string) => onFilterChange({ ...filters, channel: v }),
                icon: <Tv className="w-3.5 h-3.5 text-slate-400" />,
              },
              {
                label: 'Platform',
                tooltip: 'Platform — Meta, Google, TikTok, etc.',
                value: filters.platform,
                options: availableOptions.platform ?? [],
                onChange: (v: string) => onFilterChange({ ...filters, platform: v }),
                renderOption: (option: string) => <PlatformIcon platform={option} compact />,
                icon: <MonitorSmartphone className="w-3.5 h-3.5 text-slate-400" />,
              },
              {
                label: 'Quarter',
                tooltip: 'Quarter — Campaign quarter (e.g. Q1, Q2)',
                value: filters.quarter,
                options: availableOptions.quarter ?? [],
                onChange: (v: string) => onFilterChange({ ...filters, quarter: v }),
                icon: <CalendarDays className="w-3.5 h-3.5 text-slate-400" />,
              },
              {
                label: 'Market',
                tooltip: 'Market — Country or region',
                value: filters.market,
                options: availableOptions.market ?? [],
                onChange: (v: string) => onFilterChange({ ...filters, market: v }),
                renderOption: (option: string) => (
                  <span className="inline-flex items-center gap-1.5">
                    <FlagIcon countryCode={option} className="w-4 h-auto rounded-sm" />
                    <span>{option}</span>
                  </span>
                ),
                renderMultiDisplay: (selected: string[]) =>
                  selected.length <= 2 ? (
                    <span className="flex items-center gap-1.5">
                      {selected.map((m) => (
                        <span key={m} className="inline-flex items-center gap-1">
                          <FlagIcon countryCode={m} className="w-3.5 h-auto rounded-[2px]" />
                          {m}
                        </span>
                      ))}
                    </span>
                  ) : selected.length <= 4 ? (
                    <span className="flex items-center justify-center gap-1 w-full">
                      {selected.map((m) => (
                        <FlagIcon key={m} countryCode={m} className="w-3.5 h-auto rounded-[2px]" />
                      ))}
                    </span>
                  ) : (
                    `${selected.length} markets`
                  ),
                searchable: true,
                sortSelectedFirst: true,
                icon: <Globe className="w-3.5 h-3.5 text-slate-400" />,
              },
              {
                label: 'Business Unit',
                tooltip: 'Business Unit — Ford, Lincoln, etc.',
                value: filters.business_unit,
                options: availableOptions.business_unit ?? [],
                onChange: (v: string) => onFilterChange({ ...filters, business_unit: v }),
                icon: <Building2 className="w-3.5 h-3.5 text-slate-400" />,
              },
              {
                label: 'Nameplate',
                tooltip: 'Nameplate — Vehicle model (e.g. Mustang, Explorer)',
                value: filters.nameplate,
                options: availableOptions.nameplate ?? [],
                onChange: (v: string) => onFilterChange({ ...filters, nameplate: v }),
                icon: <Car className="w-3.5 h-3.5 text-slate-400" />,
              },
              {
                label: 'Format Type',
                tooltip: 'Format Type — Ad format (e.g. Single Image, Carousel)',
                value: filters.format,
                options: availableOptions.format ?? [],
                onChange: (v: string) => onFilterChange({ ...filters, format: v }),
                icon: <LayoutGrid className="w-3.5 h-3.5 text-slate-400" />,
              },
              {
                label: 'Plan ID',
                tooltip: 'Plan ID — Media plan identifier',
                value: filters.plan_id,
                options: availableOptions.plan_id ?? [],
                onChange: (v: string) => onFilterChange({ ...filters, plan_id: v }),
                searchable: true,
                icon: <Hash className="w-3.5 h-3.5 text-slate-400" />,
              },
            ].map(
              ({
                label,
                tooltip,
                value,
                options,
                onChange,
                renderOption,
                renderMultiDisplay,
                searchable,
                sortSelectedFirst,
                icon,
              }) => (
                <div key={label} className="flex-1 min-w-0">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <CustomSelect
                          label={label}
                          placeholder={label}
                          value={value}
                          options={options}
                          onChange={onChange}
                          renderOption={renderOption}
                          renderMultiDisplay={renderMultiDisplay}
                          searchable={searchable}
                          sortSelectedFirst={sortSelectedFirst}
                          icon={icon}
                          showLabel={false}
                          multi
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">{tooltip}</TooltipContent>
                  </Tooltip>
                </div>
              ),
            )}

            {/* Team — same CustomSelect style as all other filters */}
            <div className="flex-1 min-w-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <CustomSelect
                      label="Team"
                      placeholder="Team"
                      value={selectedTeam ?? ''}
                      options={[...VALID_TEAM_NAMES]}
                      onChange={handleTeamChange}
                      icon={<Users className="w-3.5 h-3.5 text-slate-400" />}
                      showLabel={false}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Team — Column preset for your team</TooltipContent>
              </Tooltip>
            </div>

            {/* Clear filters — always rendered so nothing shifts, blue when active */}
            <button
              onClick={totalActiveFilters > 0 ? handleClearAll : undefined}
              disabled={totalActiveFilters === 0}
              title="Clear filters"
              className={`h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-lg border transition-all ${
                totalActiveFilters > 0
                  ? 'bg-[#0014cc] border-[#0014cc] text-white hover:bg-[#0014cc]/90 cursor-pointer'
                  : 'border-slate-200 text-slate-300 cursor-not-allowed'
              }`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
